/**
 * BundleProductSelector — 2D bundle selector.
 *
 * A×B selection resolves to a pre-baked variants map (rendered in Liquid),
 * then updates subscription/onetime option datasets in <purchase-options>.
 */
class BundleProductSelector extends HTMLElement {
  constructor() {
    super();
    this.handlePillClick = this.handlePillClick.bind(this);
    this.selection = { a: null, b: null };
    this.prefetchedGalleryUrls = new Set();
    this.prefetchScheduled = false;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.productMap = this.parseProductMap();
    this.variantsMap = this.parseVariantsMap();
    this.enableDynamicCardContent = this.dataset.enableDynamicCardContent === 'true';
    this.mappingMode = this.dataset.mappingMode === 'a_only' ? 'a_only' : 'a_b';

    this.distributePills();
    this.pills = this.querySelectorAll('.bundle-pill');
    this.attachListeners();
    this.initActiveState();
    this.onSelectionChange();
    this.schedulePrefetchLikelyGalleries();
  }

  parseProductMap() {
    try {
      return JSON.parse(this.dataset.productMap || '{}');
    } catch {
      console.error('[BundleProductSelector] Invalid product-map JSON');
      return {};
    }
  }

  parseVariantsMap() {
    const mapScript = this.querySelector('[data-bundle-variants-map]');
    if (!mapScript) return {};

    try {
      return JSON.parse(mapScript.textContent || '{}');
    } catch {
      console.error('[BundleProductSelector] Invalid variants-map JSON');
      return {};
    }
  }

  distributePills() {
    const source = this.querySelector('.bundle-selector__pill-source');
    if (!source) return;

    source.querySelectorAll('.bundle-pill').forEach((pill) => {
      const target = this.querySelector(`[data-group-pills="${pill.dataset.group}"]`);
      if (target) target.appendChild(pill);
    });

    // Remove pills that don't have a rendered target group (e.g. Group B in A-only mode).
    source.querySelectorAll('.bundle-pill').forEach((pill) => pill.remove());
  }

  attachListeners() {
    this.pills.forEach((pill) => {
      pill.addEventListener('click', this.handlePillClick);
    });
  }

  handlePillClick(event) {
    event.preventDefault();
    const pill = event.currentTarget;
    const group = pill.dataset.group;
    const index = pill.dataset.pillIndex;
    if (!group || !index || this.selection[group] === index) return;

    this.setActivePill(group, index);
    this.onSelectionChange();
  }

  initActiveState() {
    this.pills.forEach((pill) => {
      if (pill.classList.contains('bundle-pill--active')) {
        this.selection[pill.dataset.group] = pill.dataset.pillIndex;
      }
    });

    ['a', 'b'].forEach((group) => {
      if (this.mappingMode === 'a_only' && group === 'b') return;
      if (this.selection[group]) return;
      const first = this.querySelector(`.bundle-pill[data-group="${group}"]`);
      if (first) this.setActivePill(group, first.dataset.pillIndex);
    });

    if (this.mappingMode === 'a_only' && !this.selection.b) {
      this.selection.b = '1';
    }
  }

  setActivePill(group, index) {
    this.selection[group] = index;

    this.pills.forEach((pill) => {
      if (pill.dataset.group !== group) return;
      const active = pill.dataset.pillIndex === index;
      pill.classList.toggle('bundle-pill--active', active);
      pill.setAttribute('aria-pressed', String(active));
      pill.setAttribute('aria-checked', String(active));
    });
  }

  getSelectedPill(group) {
    if (!this.selection[group]) return null;
    return this.querySelector(
      `.bundle-pill[data-group="${group}"][data-pill-index="${this.selection[group]}"]`
    );
  }

  getMatrixEntry() {
    const { a, b } = this.selection;
    if (!a) return { key: null, entry: null };

    if (this.mappingMode === 'a_only') {
      const fallbackPrefix = `a${a}b`;
      const fallbackKey = Object.keys(this.variantsMap || {}).find((mapKey) => mapKey.startsWith(fallbackPrefix));
      const resolvedB = b || fallbackKey?.replace(fallbackPrefix, '') || '1';
      const key = `a${a}b${resolvedB}`;

      return {
        key,
        entry: this.variantsMap[key] || (fallbackKey ? this.variantsMap[fallbackKey] : null) || null,
      };
    }

    if (!b) return { key: null, entry: null };

    const key = `a${a}b${b}`;
    return {
      key,
      entry: this.variantsMap[key] || null,
    };
  }

  getPurchaseOptionsElement() {
    const form = this.closest('form') || document.querySelector('form.purchase-options');
    if (!form) return null;
    return form.querySelector('purchase-options');
  }

  getPreferredEntry(entries, index) {
    if (!Array.isArray(entries) || !entries.length) return null;
    return entries[index] || entries[entries.length - 1] || null;
  }

  parsePositiveInt(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  parseBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
  }

  applyCampaignPrice(basePrice, input) {
    const campaignEnabled = this.parseBoolean(input?.dataset?.campaignEnabled);
    const campaignExtraDiscount = Math.min(
      100,
      Math.max(0, Number(input?.dataset?.campaignExtraDiscount || 0))
    );

    if (!campaignEnabled || campaignExtraDiscount <= 0) {
      return basePrice;
    }

    return Math.round((basePrice * (100 - campaignExtraDiscount)) / 100);
  }

  getCampaignBadgeCompareDiscount(input) {
    if (!this.parseBoolean(input?.dataset?.campaignEnabled)) return 0;
    return Math.max(0, Number(input?.dataset?.campaignBadgeCompareDiscount || 0));
  }

  updateSavingsBadges(option, selector, compareClass, savingsAmount, comparePrice, amountPrice, input) {
    const savePrefix = this.getSavingsPrefix(option, 'Save');
    const badgeCompareDiscount = this.getCampaignBadgeCompareDiscount(input);
    const badgeCompareSavings =
      badgeCompareDiscount > 0 ? Math.round((Number(comparePrice || 0) * badgeCompareDiscount) / 100) : 0;

    option.querySelectorAll(selector).forEach((badge) => {
      if (savingsAmount > 0) {
        badge.textContent = `${savePrefix} ${this.formatMoney(savingsAmount)}`;
        badge.dataset.amount = String(savingsAmount);
        badge.dataset.amountPrice = String(amountPrice);
        badge.style.display = '';

        if (badgeCompareSavings > 0) {
          const compareNode = document.createElement('span');
          compareNode.className = compareClass;
          compareNode.textContent = this.formatMoney(badgeCompareSavings);
          badge.appendChild(compareNode);
        }
      } else {
        badge.style.display = 'none';
      }
    });
  }

  inferCountFromVariantTitle(title) {
    if (typeof title !== 'string' || !title) return 0;
    const match = title.toLowerCase().match(/(\d+)\s*(month|delivery|bottle|jar|supply)/);
    return match ? this.parsePositiveInt(match[1]) : 0;
  }

  findEntryByCount(entries, desiredCount, keyName) {
    if (!Array.isArray(entries) || !entries.length || !desiredCount) return null;
    const exactMatch = entries.find((entry) => this.parsePositiveInt(entry?.[keyName]) === desiredCount);
    if (exactMatch) return exactMatch;

    return entries.find((entry) => this.inferCountFromVariantTitle(entry?.variantTitle || '') === desiredCount) || null;
  }

  formatMoney(cents) {
    if (typeof cents === 'undefined' || cents === null) return '';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  formatRoundedMoney(cents) {
    if (typeof cents === 'undefined' || cents === null) return '';

    const roundedCents = Math.round(Number(cents || 0) / 100) * 100;
    return this.formatMoney(roundedCents).replace(/([.,]00)(?!.*\d)/, '');
  }

  getSavingsPrefix(container, fallback = 'Save') {
    const badge = container.querySelector('.subscription-option__savings-badge, .onetime-option__savings-badge');
    if (!badge) return fallback;
    if (!badge.dataset.savePrefix) {
      const match = badge.textContent.trim().match(/^([^\d$€£]*Save[^\d$€£]*)/i);
      badge.dataset.savePrefix = (match?.[1] || fallback).trim();
    }
    return badge.dataset.savePrefix || fallback;
  }

  setGalleryMediaDataset(input, galleryMedia) {
    if (!input) return;

    if (!galleryMedia || typeof galleryMedia !== 'object') {
      delete input.dataset.galleryMedia;
      return;
    }

    try {
      input.dataset.galleryMedia = JSON.stringify(galleryMedia);
    } catch (error) {
      console.warn('[BundleProductSelector] Failed to serialize gallery media payload', error);
      delete input.dataset.galleryMedia;
    }
  }

  shouldPrefetchGallery() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return true;

    if (connection.saveData) return false;
    if (typeof connection.effectiveType === 'string' && /(^|\b)2g\b/.test(connection.effectiveType)) {
      return false;
    }

    return true;
  }

  scheduleIdleWork(callback, timeout = 1200) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout });
      return;
    }

    window.setTimeout(callback, 120);
  }

  collectGalleryUrls(galleryMedia, limitPerBucket = 2) {
    if (!galleryMedia || typeof galleryMedia !== 'object') return [];

    const buckets = ['desktop', 'mobile', 'both'];
    const urls = [];
    buckets.forEach((bucketKey) => {
      const bucket = Array.isArray(galleryMedia[bucketKey]) ? galleryMedia[bucketKey] : [];
      bucket.slice(0, limitPerBucket).forEach((item) => {
        if (item?.src) urls.push(item.src);
      });
    });

    return urls;
  }

  getLikelyVariantEntries() {
    const keys = Object.keys(this.variantsMap || {});
    if (!keys.length) return [];

    const activeKey = this.selection.a && this.selection.b
      ? `a${this.selection.a}b${this.selection.b}`
      : null;

    const prioritizedKeys = [];
    if (activeKey) prioritizedKeys.push(activeKey);

    keys.forEach((key) => {
      if (prioritizedKeys.includes(key)) return;
      if (!activeKey) {
        prioritizedKeys.push(key);
        return;
      }

      const sameA = key.startsWith(`a${this.selection.a}b`);
      const sameB = key.includes(`b${this.selection.b}`);
      if (sameA || sameB) prioritizedKeys.push(key);
    });

    keys.forEach((key) => {
      if (!prioritizedKeys.includes(key)) prioritizedKeys.push(key);
    });

    return prioritizedKeys.slice(0, 8).map((key) => this.variantsMap[key]).filter(Boolean);
  }

  prefetchGalleryImage(url) {
    if (!url || this.prefetchedGalleryUrls.has(url)) return;
    this.prefetchedGalleryUrls.add(url);

    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  schedulePrefetchLikelyGalleries() {
    if (this.prefetchScheduled || !this.shouldPrefetchGallery()) return;
    this.prefetchScheduled = true;

    this.scheduleIdleWork(() => {
      this.prefetchScheduled = false;
      const entries = this.getLikelyVariantEntries();
      entries.forEach((entry) => {
        const candidates = [
          entry?.onetime?.[0],
          entry?.subscription?.[0]
        ].filter(Boolean);

        candidates.forEach((candidate) => {
          this.collectGalleryUrls(candidate.galleryMedia, 2).forEach((url) => this.prefetchGalleryImage(url));
        });
      });
    });
  }

  updateSubscriptionOption(input, data) {
    const months = Number(data.months) > 0 ? Number(data.months) : 1;
    const rawAllocationPrice = Number(data.allocationPrice || data.price || 0);
    const allocationComparePrice = Number(data.allocationComparePrice || data.comparePrice || rawAllocationPrice);
    const allocationPrice = this.applyCampaignPrice(rawAllocationPrice, input);
    const discountPercentage =
      allocationComparePrice > allocationPrice
        ? Math.round(((allocationComparePrice - allocationPrice) * 100) / allocationComparePrice)
        : 0;

    input.value = String(data.variantId || '');
    input.dataset.variantId = String(data.variantId || '');
    input.dataset.sellingPlan = String(data.sellingPlanId || '');
    input.dataset.months = String(months);
    input.dataset.quantity = String(months);
    input.dataset.planPrice = String(allocationPrice);
    input.dataset.planComparePrice = String(allocationComparePrice);
    input.dataset.discountPercentage = String(discountPercentage);
    input.dataset.basePrice = String(data.price || allocationPrice);
    input.dataset.variantTitle = data.variantTitle || '';
    if (data.variantImage) {
      input.dataset.variantImage = data.variantImage;
    } else {
      delete input.dataset.variantImage;
    }
    this.setGalleryMediaDataset(input, data.galleryMedia);

    const option = input.closest('.subscription-option');
    if (!option) return;

    const monthlyPrice = Math.round(allocationPrice / months);
    const monthlyCompare = Math.round(allocationComparePrice / months);
    const savings = Math.max(0, allocationComparePrice - allocationPrice);

    const strong = option.querySelector('.subscription-option__price strong');
    if (strong) {
      const suffix = strong.textContent.replace(/^[^/]+/, '');
      strong.textContent = `${this.formatRoundedMoney(monthlyPrice)}${suffix}`;
    }

    const compare = option.querySelector('.subscription-option__original-price');
    if (compare) {
      if (monthlyCompare > monthlyPrice) {
        compare.textContent = this.formatMoney(monthlyCompare);
        compare.style.display = '';
      } else {
        compare.style.display = 'none';
      }
    }

    this.updateSavingsBadges(
      option,
      '.subscription-option__savings-badge',
      'subscription-option__savings-compare',
      savings,
      allocationComparePrice,
      monthlyPrice,
      input
    );
  }

  updateOnetimeOption(input, data) {
    const quantity = Number(data.quantity) > 0 ? Number(data.quantity) : 1;
    const price = Number(data.price || 0);
    const rawTotalPrice = Number(data.totalPrice || price * quantity);
    const totalPrice = this.applyCampaignPrice(rawTotalPrice, input);
    const comparePrice = Number(data.comparePrice || totalPrice);
    const discountPercentage =
      comparePrice > totalPrice ? Math.round(((comparePrice - totalPrice) * 100) / comparePrice) : 0;

    input.value = String(data.variantId || '');
    input.dataset.variantId = String(data.variantId || '');
    input.dataset.quantity = String(quantity);
    input.dataset.totalPrice = String(totalPrice);
    input.dataset.comparePrice = String(comparePrice);
    input.dataset.discountPercentage = String(discountPercentage);
    input.dataset.basePrice = String(price || totalPrice);
    input.dataset.variantTitle = data.variantTitle || '';
    if (data.variantImage) {
      input.dataset.variantImage = data.variantImage;
    } else {
      delete input.dataset.variantImage;
    }
    this.setGalleryMediaDataset(input, data.galleryMedia);

    const option = input.closest('.onetime-option');
    if (!option) return;

    const strong = option.querySelector('.onetime-option__price strong');
    if (strong) strong.textContent = this.formatMoney(totalPrice);

    const compare = option.querySelector('.onetime-option__original-price');
    if (compare) {
      if (comparePrice > totalPrice) {
        compare.textContent = this.formatMoney(comparePrice);
        compare.style.display = '';
      } else {
        compare.style.display = 'none';
      }
    }

    const savings = Math.max(0, comparePrice - totalPrice);
    this.updateSavingsBadges(
      option,
      '.onetime-option__savings-badge',
      'onetime-option__savings-compare',
      savings,
      comparePrice,
      totalPrice,
      input
    );
  }

  populatePurchaseOptions(entry) {
    const purchaseOptions = this.getPurchaseOptionsElement();
    if (!purchaseOptions) return false;

    const subInputs = purchaseOptions.querySelectorAll('.subscription-option__input');
    const otpInputs = purchaseOptions.querySelectorAll('.onetime-option__input');
    const subEntries = Array.isArray(entry.subscription) ? entry.subscription : [];
    const otpEntries = Array.isArray(entry.onetime) ? entry.onetime : [];

    subInputs.forEach((input, index) => {
      // Match by editor-configured delivery months first, then fall back to list order.
      const desiredMonths = this.parsePositiveInt(input.dataset.months || input.dataset.quantity);
      const data = this.findEntryByCount(subEntries, desiredMonths, 'months') || this.getPreferredEntry(subEntries, index);
      if (data) this.updateSubscriptionOption(input, data);
    });

    otpInputs.forEach((input, index) => {
      const desiredQuantity = this.parsePositiveInt(input.dataset.quantity || input.dataset.months);
      const data = this.findEntryByCount(otpEntries, desiredQuantity, 'quantity') || this.getPreferredEntry(otpEntries, index);
      if (data) this.updateOnetimeOption(input, data);
    });

    if (subEntries.length > 0 && typeof purchaseOptions.selectSubscription === 'function') {
      purchaseOptions.selectSubscription();
    } else {
      const onetimeTab = purchaseOptions.querySelector('.purchase-options__tab-nav--onetime');
      if (onetimeTab && !onetimeTab.classList.contains('active')) onetimeTab.click();
      if (typeof purchaseOptions.updatePurchaseType === 'function') purchaseOptions.updatePurchaseType();
    }

    return true;
  }

  resolveFallbackCardImage(group, entry, imageElement) {
    if (group !== 'b') return imageElement?.dataset.defaultSrc || '';
    return (
      entry?.onetime?.[0]?.variantImage || entry?.subscription?.[0]?.variantImage || imageElement?.dataset.defaultSrc || ''
    );
  }

  ensureCardImageElement(mediaElement) {
    if (!mediaElement) return null;

    const existingImage = mediaElement.querySelector('[data-dynamic-card-image]');
    if (existingImage) return existingImage;

    const createdImage = document.createElement('img');
    createdImage.setAttribute('data-dynamic-card-image', '');
    createdImage.setAttribute('loading', 'lazy');
    createdImage.setAttribute('width', '180');
    createdImage.setAttribute('height', '180');
    createdImage.alt = '';
    mediaElement.appendChild(createdImage);
    return createdImage;
  }

  updateDynamicCardGroup(group, entry) {
    const card = this.querySelector(`[data-dynamic-card-group="${group}"]`);
    if (!card) return;

    const selectedPill = this.getSelectedPill(group);
    if (!selectedPill) return;

    const media = card.querySelector('[data-dynamic-card-media]');
    const image = media ? this.ensureCardImageElement(media) : null;
    if (image) {
      const pillImage = selectedPill.dataset.cardImage || '';
      const fallbackImage = this.resolveFallbackCardImage(group, entry, image);
      const nextImage = pillImage || fallbackImage;
      if (nextImage) image.src = nextImage;

      const nextAlt =
        selectedPill.dataset.cardImageAlt ||
        selectedPill.dataset.cardTitle ||
        selectedPill.dataset.pillLabel ||
        image.dataset.defaultAlt ||
        '';
      image.alt = nextAlt;
    }

    const titleElement = card.querySelector('[data-dynamic-card-title]');
    if (titleElement) {
      const nextTitle = selectedPill.dataset.cardTitle || selectedPill.dataset.pillLabel || titleElement.dataset.defaultText || '';
      titleElement.textContent = nextTitle;
    }

    const subtitleElement = card.querySelector('[data-dynamic-card-subtitle]');
    if (subtitleElement) {
      const nextSubtitle = selectedPill.dataset.cardSubtitle || subtitleElement.dataset.defaultText || '';
      subtitleElement.textContent = nextSubtitle;
      subtitleElement.hidden = !nextSubtitle;
    }
  }

  updateDynamicCardContent(entry) {
    if (!this.enableDynamicCardContent) return;
    ['a', 'b'].forEach((group) => this.updateDynamicCardGroup(group, entry));
  }

  onSelectionChange() {
    const { key, entry } = this.getMatrixEntry();
    if (!key) return;

    if (!entry) {
      console.warn(`[BundleProductSelector] No variants map found for ${key}`);
      return;
    }

    const updated = this.populatePurchaseOptions(entry);
    if (!updated) {
      console.warn('[BundleProductSelector] No purchase-options element found');
      return;
    }

    this.updateDynamicCardContent(entry);
    window.dispatchEvent(new CustomEvent('variant:change', { detail: { source: 'bundle-product-selector', key } }));
    this.schedulePrefetchLikelyGalleries();
  }

  cleanup() {
    if (!this.pills) return;
    this.pills.forEach((pill) => {
      pill.removeEventListener('click', this.handlePillClick);
    });
  }
}

if (!customElements.get('bundle-product-selector')) {
  customElements.define('bundle-product-selector', BundleProductSelector);
}
